// @flow

import React from "react";
import Section from "../../components/Section";
import EditableForm from "../../components/EditableForm";
import FormRow from "../../components/FormRow";
import EditableField from "../../components/EditableField";

import {
  COMPANY__ROLE,
  COMPANY__NAME,
  COMPANY__STREET,
  COMPANY__ZIP_CODE,
  COMPANY__CITY,
  COMPANY__LOCATION,
  COMPANY__INDUSTRY,
  COMPANY__TURNOVER_CLASS,
  COMPANY__FOUNDING_YEAR,
  COMPANY__ANNUAL_NET_PROFIT_OR_LOSS_CLASS,
  COMPANY__NEGATIVE_EQUITY,
  COMPANY__CONSIDER_SUBSIDY,
  COMPANY__INNOVATIVE,
  COMPANY__ENERGY_EFFICIENT,
  COMPANY__CONTACT_PERSON,
  COMPANY__CONTACT_PERSON_COMPANY__ROLE,
  COMPANY__CONTACT_PERSON_EMAIL,
  COMPANY__CONTACT_PERSON_PHONE_NUMBER,
  COMPANY__CONTACT_PERSON_NAME
} from "./formFields";

import {
  mapCompanyAnnualNetProfit,
  mapCompanyIndustry,
  mapCompanyRole,
  mapBoolean,
  mapCompanyTurnover,
  mapRole
} from "./mappers";
import { chooseType } from "../../data/dictionary";

type CompanyProps = {
  initialValues: Object<any>,
  isBankAdvisorInquiry: boolean,
  type: "customer" | "bankAdvisor",
  site: "buba" | "bank" | "customer"
};

const Company = ({ initialValues, isBankAdvisorInquiry, type, site }: CompanyProps) => {
  const contactPerson = initialValues[COMPANY__CONTACT_PERSON];
  const formattedInitialValues = {
    ...initialValues,
    [COMPANY__CONTACT_PERSON_NAME]:
      contactPerson && `${contactPerson.firstName} ${contactPerson.lastName}`,
    [COMPANY__CONTACT_PERSON_COMPANY__ROLE]: contactPerson && contactPerson.position,
    [COMPANY__CONTACT_PERSON_EMAIL]: contactPerson && contactPerson.email,
    [COMPANY__CONTACT_PERSON_PHONE_NUMBER]: contactPerson && contactPerson.phoneNumber,
    [COMPANY__LOCATION]: `${initialValues[COMPANY__ZIP_CODE]} ${initialValues[COMPANY__CITY]}`
  };

  return (
    <Section title="Unternehmensdetails" reducedMargin numbered>
      <EditableForm initialValues={formattedInitialValues} isLocked>
        {!isBankAdvisorInquiry && (
          <FormRow separator>
            <EditableField
              fieldControlProps={{
                name: COMPANY__ROLE,
                caption: () => chooseType(site, type) && chooseType(site, type).entrepreneurType
              }}
              displayValue={mapRole}
            />
          </FormRow>
        )}
        <FormRow separator>
          <EditableField
            fieldControlProps={{
              name: COMPANY__NAME,
              caption: () => chooseType(site, type) && chooseType(site, type).companyLabel
            }}
          />
          <EditableField
            fieldControlProps={{
              name: COMPANY__STREET,
              caption: () => "Adresse"
            }}
          />
          <EditableField
            fieldControlProps={{
              name: COMPANY__LOCATION,
              caption: () => "Ort"
            }}
          />
        </FormRow>
        <FormRow
          separator={
            isBankAdvisorInquiry ||
            (!isBankAdvisorInquiry && initialValues[COMPANY__CONSIDER_SUBSIDY])
          }
        >
          <EditableField
            fieldControlProps={{
              name: COMPANY__INDUSTRY,
              caption: () => "Branche"
            }}
            displayValue={mapCompanyIndustry}
          />
          <EditableField
            fieldControlProps={{
              name: COMPANY__TURNOVER_CLASS,
              caption: () => "Umsatzklasse"
            }}
            displayValue={mapCompanyTurnover}
          />
          <EditableField
            fieldControlProps={{
              name: COMPANY__FOUNDING_YEAR,
              caption: () => "Gründungsjahr"
            }}
          />
        </FormRow>
        {isBankAdvisorInquiry && (
          <>
            <FormRow separator>
              <EditableField
                fieldControlProps={{
                  name: COMPANY__CONTACT_PERSON_NAME,
                  caption: () => "Ansprechpartner im Unternehmen"
                }}
              />
              <EditableField
                fieldControlProps={{
                  name: COMPANY__CONTACT_PERSON_COMPANY__ROLE,
                  caption: () => "Rolle im Unternehmen"
                }}
                displayValue={mapCompanyRole}
              />
              <div />
            </FormRow>
            <FormRow>
              <EditableField
                fieldControlProps={{
                  name: COMPANY__CONTACT_PERSON_EMAIL,
                  caption: () => "E-mail"
                }}
              />
              <EditableField
                fieldControlProps={{
                  name: COMPANY__CONTACT_PERSON_PHONE_NUMBER,
                  caption: () => "Telefon"
                }}
              />
              <div />
            </FormRow>
          </>
        )}
        {!isBankAdvisorInquiry && initialValues[COMPANY__CONSIDER_SUBSIDY] && (
          <>
            <FormRow separator>
              <EditableField
                fieldControlProps={{
                  name: COMPANY__ANNUAL_NET_PROFIT_OR_LOSS_CLASS,
                  caption: () => "Jährlicher Überschuss"
                }}
                displayValue={mapCompanyAnnualNetProfit}
              />
              <EditableField
                fieldControlProps={{
                  name: COMPANY__NEGATIVE_EQUITY,
                  caption: () => "Hat das Unternehmen negatives Eigenkapital?"
                }}
                displayValue={mapBoolean}
              />
              <div />
            </FormRow>
            <FormRow>
              <EditableField
                fieldControlProps={{
                  name: COMPANY__INNOVATIVE,
                  caption: () =>
                    "Würde der Kunde sein Unternehmen oder Vorhaben als innovativ bzw. digital bezeichnen?"
                }}
                displayValue={mapBoolean}
              />
              <EditableField
                fieldControlProps={{
                  name: COMPANY__ENERGY_EFFICIENT,
                  caption: () =>
                    "Würde der Kunde sein Unternehmen oder Vorhaben als energieeffizient bezeichnen?"
                }}
                displayValue={mapBoolean}
              />
              <div />
            </FormRow>
          </>
        )}
      </EditableForm>
    </Section>
  );
};

export default Company;
